import React, { useCallback, useEffect, useState } from 'react';
import { BsBell } from 'react-icons/bs';
import { Check } from 'lucide-react';
import { io } from 'socket.io-client';
import useFetch from 'use-http';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { StringHelpers } from 'devyx-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../ui/dropdown-menu';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import {
  MAX_NOTIFICATIONS_IN_DROPDOWN,
  notificationAlert,
  NotificationsTypeNames,
} from '../Notifications';
import NotificationsList from '../Notifications/NotificationsList';

export interface INotificationsProps {
  _id: string;
  channel: string;
  user_id: string;
  software_id?: string | null;
  content: string;
  company_id?: string;
  read_at: string | null;
  deleted_at?: string;
  isOpen?: boolean;
  isRead?: boolean;
  created_at: string;
  updated_at: string;
  __v: number;
}

export type ReadTypeField = 'all' | 'false';

export interface IParsedContentNotification {
  type: NotificationsTypeNames;
  text_pt: string;
  text_en?: string;
  status?: string;
  is_active: boolean;
}

const DropdownNotification: React.FC = () => {
  const [notifications, setNotifications] = useState<INotificationsProps[]>([]);
  const { key } = useLocation();
  const { keycloak, initialized } = useKeycloak();
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { get, patch, response } = useFetch('/notification');

  const readNotification = useCallback(
    async (id: string) => {
      await patch(`/read/${id}`);
      setUnreadNotificationsCount(prev => prev - 1);
    },
    [patch],
  );

  const readAllNotifications = useCallback(
    async () => patch('/read-all'),
    [patch],
  );

  const getNotifications = useCallback(
    async (read: ReadTypeField = 'all') => {
      const params = {
        deleted: false,
        read,
      };

      await get(`/?${StringHelpers.encodeToQueryParams(params)}`);
      if (!response.ok) return;
      if (response.ok) {
        setUnreadNotificationsCount(response.data.unread);

        const notificationsCloneProp: INotificationsProps[] =
          response.data.docs.map((notification: INotificationsProps) => ({
            ...notification,
            isOpen: false,
            isRead: notification.read_at !== null,
          }));
        setNotifications(notificationsCloneProp);
      } else {
        toast.error('Erro ao carregar as notificações!');
      }
    },
    [get, response],
  );

  const handleNotification = useCallback(
    (notification: string) => {
      notificationAlert(notification);
      getNotifications();
    },
    [getNotifications],
  );

  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}`, {
      transports: ['websocket'],
      auth: {
        authorization: `Bearer ${keycloak?.token}`,
      },
    });

    socket.on('System', (notification: string) => {
      handleNotification(notification);
    });

    return () => {
      socket.disconnect();
    };
  }, [keycloak, handleNotification]);

  useEffect(() => {
    if (keycloak && initialized) {
      getNotifications();
    }
  }, [keycloak, initialized, getNotifications]);

  const openAndCloseNotification = useCallback(
    (id: string) => {
      const cloneNotification = notifications.map(notification => ({
        ...notification,
        isOpen: id === notification._id ? !notification.isOpen : false,
        isRead: (() => {
          if (id === notification._id) {
            if (!notification.isRead) {
              readNotification(notification._id);
            }
            return true;
          }
          return notification.isRead;
        })(),
      }));
      setNotifications(cloneNotification);
    },
    [notifications, readNotification],
  );

  const markAllAsRead = useCallback(() => {
    readAllNotifications();
    setUnreadNotificationsCount(0);
    const cloneNotifications = notifications.map(item => ({
      ...item,
      isRead: true,
    }));
    setNotifications(cloneNotifications);
  }, [notifications, readAllNotifications]);

  return (
    <DropdownMenu key={key}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={() => setCurrentTime(new Date())}
        >
          <BsBell className="h-5 w-5" />
          {unreadNotificationsCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadNotificationsCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">Notificações</DropdownMenuLabel>
          <Button
            variant="ghost"
            size="sm"
            onClick={markAllAsRead}
            className="h-auto p-1 text-xs font-normal text-muted-foreground hover:text-foreground"
          >
            <Check className="mr-1 h-3 w-3" />
            Marcar todas como lidas
          </Button>
        </div>
        <DropdownMenuSeparator />
        <ScrollArea className="h-[400px]">
          <NotificationsList
            currentTime={currentTime}
            onRead={openAndCloseNotification}
            notifications={notifications.slice(
              0,
              MAX_NOTIFICATIONS_IN_DROPDOWN,
            )}
          />
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DropdownNotification;
